// 认证状态管理
import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'

export const useAuthStore = defineStore('auth', () => {
  // 状态
  const apiKey = ref<string>('')
  const isAuthenticated = ref<boolean>(false)
  const lastAuthCheck = ref<Date | null>(null)

  // 计算属性
  const hasValidKey = computed(() => {
    return apiKey.value.length >= 16 && /^[a-zA-Z0-9_-]+$/.test(apiKey.value)
  })

  const authHeader = computed(() => {
    if (!apiKey.value) return {}
    return {
      'Authorization': `Bearer ${apiKey.value}`,
      'X-API-Key': apiKey.value
    }
  })

  // 方法
  function setApiKey(key: string) {
    apiKey.value = key.trim()
    
    // 保存到 localStorage
    if (key) {
      localStorage.setItem('menu_api_key', key)
      isAuthenticated.value = true
      lastAuthCheck.value = new Date()
    } else {
      localStorage.removeItem('menu_api_key')
      isAuthenticated.value = false
      lastAuthCheck.value = null
    }
  }

  function loadApiKey() {
    const savedKey = localStorage.getItem('menu_api_key')
    if (savedKey) {
      apiKey.value = savedKey
      isAuthenticated.value = true
      lastAuthCheck.value = new Date()
    }
  }

  function clearAuth() {
    apiKey.value = ''
    isAuthenticated.value = false
    lastAuthCheck.value = null
    localStorage.removeItem('menu_api_key')
  }

  // 验证 API 密钥
  async function validateApiKey(key?: string): Promise<boolean> {
    const keyToValidate = key || apiKey.value
    
    if (!keyToValidate) {
      return false
    }

    try {
      // 尝试调用一个需要认证的 API 端点来验证密钥
      const response = await fetch('/api/templates', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${keyToValidate}`,
          'X-API-Key': keyToValidate,
          'Content-Type': 'application/json',
        },
      })

      const isValid = response.ok
      
      if (isValid) {
        if (key) {
          setApiKey(key)
        }
        isAuthenticated.value = true
        lastAuthCheck.value = new Date()
      } else {
        if (response.status === 401) {
          // 认证失败，清除本地存储的密钥
          clearAuth()
        }
        isAuthenticated.value = false
      }

      return isValid
    } catch (error) {
      console.error('验证 API 密钥失败:', error)
      isAuthenticated.value = false
      return false
    }
  }

  // 检查认证状态
  async function checkAuthStatus(): Promise<boolean> {
    if (!apiKey.value) {
      return false
    }

    // 如果最近验证过（5分钟内），直接返回当前状态
    if (lastAuthCheck.value && Date.now() - lastAuthCheck.value.getTime() < 5 * 60 * 1000) {
      return isAuthenticated.value
    }

    return await validateApiKey()
  }

  // 获取认证头
  function getAuthHeaders(): Record<string, string> {
    if (!apiKey.value) {
      return {}
    }

    return {
      'Authorization': `Bearer ${apiKey.value}`,
      'X-API-Key': apiKey.value,
    }
  }

  // 初始化时加载保存的密钥
  loadApiKey()

  return {
    // 状态
    apiKey: readonly(apiKey),
    isAuthenticated: readonly(isAuthenticated),
    lastAuthCheck: readonly(lastAuthCheck),
    
    // 计算属性
    hasValidKey,
    authHeader,
    
    // 方法
    setApiKey,
    loadApiKey,
    clearAuth,
    validateApiKey,
    checkAuthStatus,
    getAuthHeaders,
  }
})
